import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotifications } from "../contexts/NotificationContext";
import ConfirmModal from "../components/ConfirmModal";
import { LoadingSpinner } from "../components/ui/loading-spinner";

const generateTimeOptions = () => {
  const options = [];
  for (let i = 0; i < 24; i++) {
    const hour = i.toString().padStart(2, "0");
    options.push(`${hour}:00`);
    options.push(`${hour}:30`);
  }
  return options;
};

const inputCls =
  "w-full bg-surface-container-highest border-none rounded-lg px-4 py-2.5 text-sm text-on-surface focus:ring-1 focus:ring-primary focus:outline-none placeholder:text-on-surface-variant/40 transition-all duration-200";

const labelCls =
  "block text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1";

const BusinessProfile = () => {
  const { addToastNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDeleteIndex, setProductToDeleteIndex] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingNavRef = useRef(null);
  const isDirtyRef = useRef(false);

  // Mantém ref sincronizada para uso no listener de popstate
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Intercepta cliques em links internos (elementos <a> do React Router)
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current) return;

      // Procura o elemento <a> mais próximo
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto")) return;
      if (href === location.pathname) return;

      e.preventDefault();
      e.stopPropagation();
      pendingNavRef.current = href;
      setShowUnsavedModal(true);
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname]);

  // Intercepta botão Voltar/Avançar do browser
  useEffect(() => {
    const handlePopState = (e) => {
      if (!isDirtyRef.current) return;
      // Empurra de volta para manter a URL atual
      window.history.pushState(null, "", location.pathname);
      setShowUnsavedModal(true);
      pendingNavRef.current = null; // sem destino específico (voltar)
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [location.pathname]);

  const handleConfirmLeave = () => {
    setIsDirty(false);
    isDirtyRef.current = false;
    setShowUnsavedModal(false);
    if (pendingNavRef.current) {
      navigate(pendingNavRef.current);
    } else {
      navigate(-1);
    }
    pendingNavRef.current = null;
  };

  const handleCancelLeave = () => {
    setShowUnsavedModal(false);
    pendingNavRef.current = null;
  };

  // Estado do Perfil do Negócio
  const [profile, setProfile] = useState({
    businessName: "",
    description: "",
    address: "",
  });

  // Estado dos Horários de Atendimento
  const [schedule, setSchedule] = useState([
    { day: "Segunda-feira", active: true, start: "09:00", end: "18:00" },
    { day: "Terça-feira", active: true, start: "09:00", end: "18:00" },
    { day: "Quarta-feira", active: true, start: "09:00", end: "18:00" },
    { day: "Quinta-feira", active: true, start: "09:00", end: "18:00" },
    { day: "Sexta-feira", active: true, start: "09:00", end: "18:00" },
    { day: "Sábado", active: false, start: "09:00", end: "14:00" },
    { day: "Domingo", active: false, start: "09:00", end: "14:00" },
  ]);
  const [exceptions, setExceptions] = useState("");

  // Estado dos Produtos e Serviços
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState({
    name: "",
    description: "",
    basePrice: "",
    priceVariations: [],
    extras: [],
  });

  // Estado Operacional e Logística
  const [operational, setOperational] = useState({
    salesChannel: "",
    serviceArea: "",
    shippingType: "fixed",
    shippingValue: "",
    paymentMethods: {
      pix: false,
      credit: false,
      debit: false,
      cash: false,
    },
    paymentInstructions: "",
  });

  // Estado de Observações
  const [observations, setObservations] = useState("");

  const loadBusinessProfile = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetch(`/api/business-profile`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.profile) {
          setProfile(
            data.profile.profile || {
              businessName: "",
              description: "",
              address: "",
            }
          );
          setSchedule(
            data.profile.schedule || [
              { day: "Segunda-feira", active: true, start: "09:00", end: "18:00" },
              { day: "Terça-feira", active: true, start: "09:00", end: "18:00" },
              { day: "Quarta-feira", active: true, start: "09:00", end: "18:00" },
              { day: "Quinta-feira", active: true, start: "09:00", end: "18:00" },
              { day: "Sexta-feira", active: true, start: "09:00", end: "18:00" },
              { day: "Sábado", active: false, start: "09:00", end: "14:00" },
              { day: "Domingo", active: false, start: "09:00", end: "14:00" },
            ]
          );
          setExceptions(data.profile.exceptions || "");
          setProducts(data.profile.products || []);
          setOperational(
            data.profile.operational || {
              salesChannel: "",
              serviceArea: "",
              shippingType: "fixed",
              shippingValue: "",
              paymentMethods: {
                pix: false,
                credit: false,
                debit: false,
                cash: false,
              },
              paymentInstructions: "",
            }
          );
          setObservations(data.profile.observations || "");
        }
      }
    } catch (error) {
      console.error("Erro ao carregar perfil:", error);
    } finally {
      setLoading(false);
      setIsDirty(false); // Após carregar, não há alterações pendentes
    }
  }, []);

  useEffect(() => {
    loadBusinessProfile();
  }, [loadBusinessProfile]);

  // Bloqueia fechamento/reload da aba quando há alterações não salvas
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const validateForm = () => {
    if (!profile.businessName?.trim()) {
      addToastNotification({
        id: Date.now(),
        title: "Campo Obrigatório",
        message: "O Nome do Negócio é obrigatório.",
        type: "error",
      });
      return false;
    }
    if (!profile.description?.trim()) {
      addToastNotification({
        id: Date.now(),
        title: "Campo Obrigatório",
        message: "A Descrição do negócio é obrigatória.",
        type: "error",
      });
      return false;
    }
    if (!profile.address?.trim()) {
      addToastNotification({
        id: Date.now(),
        title: "Campo Obrigatório",
        message: "O Endereço físico é obrigatório.",
        type: "error",
      });
      return false;
    }
    if (products.length === 0) {
      addToastNotification({
        id: Date.now(),
        title: "Campo Obrigatório",
        message: "Adicione pelo menos um produto ou serviço.",
        type: "error",
      });
      return false;
    }
    if (!operational.salesChannel?.trim()) {
      addToastNotification({
        id: Date.now(),
        title: "Campo Obrigatório",
        message: "O Canal de Vendas (Link) é obrigatório.",
        type: "error",
      });
      return false;
    }
    if (!operational.serviceArea?.trim()) {
      addToastNotification({
        id: Date.now(),
        title: "Campo Obrigatório",
        message: "A Área de Atuação é obrigatória.",
        type: "error",
      });
      return false;
    }
    const hasPaymentMethod = Object.values(operational.paymentMethods).some(
      (v) => v
    );
    if (!hasPaymentMethod) {
      addToastNotification({
        id: Date.now(),
        title: "Campo Obrigatório",
        message: "Selecione pelo menos uma forma de pagamento.",
        type: "error",
      });
      return false;
    }
    return true;
  };

  const saveBusinessProfile = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const profileData = {
        profile,
        schedule,
        exceptions,
        products,
        operational,
        observations,
      };

      const fullUrl = `/api/business-profile`;

      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        setIsDirty(false); // Limpa o estado de alterações não salvas
        addToastNotification({
          id: Date.now(),
          title: "Sucesso",
          message: "Perfil salvo com sucesso!",
          type: "success",
        });
        await loadBusinessProfile();
      } else {
        const errorData = await response.json();
        let errorMessage = "Erro ao salvar perfil";
        if (errorData.error && typeof errorData.error === "string") {
          errorMessage = errorData.error;
        } else if (errorData.details && typeof errorData.details === "string") {
          errorMessage = errorData.details;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = JSON.stringify(errorData);
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      addToastNotification({
        id: Date.now(),
        title: "Erro",
        message: error.message || "Erro desconhecido ao salvar perfil",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const openProductModal = (product = null, index = null) => {
    if (product) {
      setCurrentProduct({ ...product });
      setEditingProduct(index);
    } else {
      setCurrentProduct({
        name: "",
        description: "",
        basePrice: "",
        priceVariations: [],
        extras: [],
      });
      setEditingProduct(null);
    }
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setCurrentProduct({
      name: "",
      description: "",
      basePrice: "",
      priceVariations: [],
      extras: [],
    });
    setEditingProduct(null);
  };

  const saveProduct = () => {
    if (!currentProduct.name || !currentProduct.basePrice) {
      addToastNotification({
        id: Date.now(),
        title: "Atenção",
        message: "Preencha os campos obrigatórios",
        type: "warning",
      });
      return;
    }
    if (editingProduct !== null) {
      const updatedProducts = [...products];
      updatedProducts[editingProduct] = currentProduct;
      setProducts(updatedProducts);
    } else {
      setProducts([...products, currentProduct]);
    }
    closeProductModal();
    setIsDirty(true);
    addToastNotification({
      id: Date.now(),
      title: "Sucesso",
      message: "Produto salvo!",
      type: "success",
    });
  };

  const handleDeleteClick = (index) => {
    setProductToDeleteIndex(index);
    setShowDeleteModal(true);
  };

  const confirmDeleteProduct = () => {
    if (productToDeleteIndex !== null) {
      const updatedProducts = products.filter(
        (_, i) => i !== productToDeleteIndex
      );
      setProducts(updatedProducts);
      setIsDirty(true);
      addToastNotification({
        id: Date.now(),
        title: "Sucesso",
        message: "Produto excluído",
        type: "success",
      });
      setShowDeleteModal(false);
      setProductToDeleteIndex(null);
    }
  };

  const addPriceVariation = () => {
    setCurrentProduct({
      ...currentProduct,
      priceVariations: [
        ...currentProduct.priceVariations,
        { condition: "", price: "" },
      ],
    });
  };

  const updatePriceVariation = (index, field, value) => {
    const updated = [...currentProduct.priceVariations];
    updated[index][field] = value;
    setCurrentProduct({ ...currentProduct, priceVariations: updated });
  };

  const removePriceVariation = (index) => {
    const updated = currentProduct.priceVariations.filter(
      (_, i) => i !== index
    );
    setCurrentProduct({ ...currentProduct, priceVariations: updated });
  };

  const addExtra = () => {
    setCurrentProduct({
      ...currentProduct,
      extras: [...currentProduct.extras, { name: "", price: "" }],
    });
  };

  const updateExtra = (index, field, value) => {
    const updated = [...currentProduct.extras];
    updated[index][field] = value;
    setCurrentProduct({ ...currentProduct, extras: updated });
  };

  const removeExtra = (index) => {
    const updated = currentProduct.extras.filter((_, i) => i !== index);
    setCurrentProduct({ ...currentProduct, extras: updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="page-enter">
      <div className="p-6 space-y-8 pb-32 bg-background min-h-screen font-body">
        {/* Header */}
        <header className="mb-10 max-w-3xl">
          <h1 className="font-headline text-3xl font-extrabold text-on-surface tracking-tight mb-2">
            Perfil do Negócio
          </h1>
          <p className="text-on-surface-variant text-sm opacity-80">
            Preencha todos os campos abaixo para configurar seu assistente IA corretamente.
          </p>
        </header>

        <div className="space-y-8">
          {/* SECTION 1: Configurações de Identidade */}
          <section className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <span className="material-symbols-outlined">store</span>
              </div>
              <h2 className="font-headline text-xl font-bold text-on-surface">
                1. Configurações de Identidade
              </h2>
            </div>
            <div className="space-y-6">
              <div>
                <label className={labelCls}>Nome do Negócio *</label>
                <input
                  type="text"
                  value={profile.businessName}
                  onChange={(e) => {
                    setProfile({ ...profile, businessName: e.target.value });
                    setIsDirty(true);
                  }}
                  placeholder="Ex: Sushi Premium"
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>
                  O que fazemos e funcionamento (Em resumo) *
                </label>
                <textarea
                  value={profile.description}
                  onChange={(e) => {
                    setProfile({ ...profile, description: e.target.value });
                    setIsDirty(true);
                  }}
                  placeholder="Descreva brevemente sua empresa e como ela funciona..."
                  rows={5}
                  className={inputCls + " resize-none leading-relaxed"}
                />
              </div>

              <div>
                <label className={labelCls}>Endereço Físico *</label>
                <div className="relative">
                  <span className="absolute top-1/2 -translate-y-1/2 left-3 text-on-surface-variant/60">
                    <span className="material-symbols-outlined text-xl">location_on</span>
                  </span>
                  <input
                    type="text"
                    value={profile.address}
                    onChange={(e) => {
                      setProfile({ ...profile, address: e.target.value });
                      setIsDirty(true);
                    }}
                    placeholder="Rua, número, bairro, cidade - Estado"
                    className={inputCls + " pl-10"}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 2: Horários de Atendimento */}
          <section className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                <span className="material-symbols-outlined">schedule</span>
              </div>
              <h2 className="font-headline text-xl font-bold text-on-surface">
                2. Horários de Atendimento
              </h2>
            </div>
            <div className="space-y-6">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                Grade Semanal
              </p>
              <div className="space-y-3">
                {schedule.map((day, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap items-center justify-between gap-2 p-3 sm:p-4 bg-surface-container-highest rounded-lg hover:bg-surface-container-high transition-colors"
                  >
                    <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                      {/* Toggle switch */}
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={day.active}
                          onChange={() => {
                            const updated = [...schedule];
                            updated[index].active = !updated[index].active;
                            setSchedule(updated);
                            setIsDirty(true);
                          }}
                        />
                        <div className="w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                      <span className="text-sm font-medium text-on-surface w-24 sm:w-32 truncate">
                        {day.day}
                      </span>
                    </div>

                    {day.active ? (
                      <div className="flex items-center gap-1 sm:gap-3 text-on-surface-variant text-sm flex-shrink-0">
                        <select
                          value={day.start}
                          onChange={(e) => {
                            const updated = [...schedule];
                            updated[index].start = e.target.value;
                            setSchedule(updated);
                            setIsDirty(true);
                          }}
                          className="bg-surface-container border-none rounded-lg px-2 sm:px-3 py-1.5 text-sm text-on-surface focus:ring-1 focus:ring-primary focus:outline-none cursor-pointer"
                        >
                          {generateTimeOptions().map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                        <span className="text-on-surface-variant text-xs">até</span>
                        <select
                          value={day.end}
                          onChange={(e) => {
                            const updated = [...schedule];
                            updated[index].end = e.target.value;
                            setSchedule(updated);
                            setIsDirty(true);
                          }}
                          className="bg-surface-container border-none rounded-lg px-2 sm:px-3 py-1.5 text-sm text-on-surface focus:ring-1 focus:ring-primary focus:outline-none cursor-pointer"
                        >
                          {generateTimeOptions().map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <span className="text-sm italic text-zinc-500">
                        Fechado
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <label className={labelCls + " text-emerald-500/80"}>
                  Feriados e Exceções
                </label>
                <textarea
                  value={exceptions}
                  onChange={(e) => { setExceptions(e.target.value); setIsDirty(true); }}
                  placeholder='Ex: "Fechado no Natal e Ano Novo" ou "Aberto para almoço no dia das mães"'
                  rows={3}
                  className={inputCls + " resize-none"}
                />
              </div>
            </div>
          </section>

          {/* SECTION 3: Produtos e Serviços */}
          <section className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400">
                  <span className="material-symbols-outlined">inventory_2</span>
                </div>
                <h2 className="font-headline text-xl font-bold text-on-surface">
                  3. Produtos e Serviços *
                </h2>
              </div>
              <button
                onClick={() => openProductModal()}
                className="bg-primary text-on-primary hover:bg-primary/90 rounded-lg px-5 py-2.5 font-semibold text-sm flex items-center gap-2 transition-colors"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                Adicionar Item
              </button>
            </div>

            {products.length === 0 ? (
              <div className="border-2 border-dashed border-white/10 rounded-xl p-12 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-surface-container-highest rounded-full flex items-center justify-center mb-4 text-zinc-500">
                  <span className="material-symbols-outlined text-4xl">inventory_2</span>
                </div>
                <p className="text-on-surface-variant mb-2">
                  Nenhum produto cadastrado ainda.
                </p>
                <p className="text-error text-xs mb-4">
                  É necessário cadastrar pelo menos um produto.
                </p>
                <button
                  onClick={() => openProductModal()}
                  className="text-primary font-semibold hover:underline text-sm"
                >
                  Adicionar primeiro produto
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((product, index) => (
                  <div
                    key={index}
                    className="p-4 bg-surface-container-highest rounded-lg border border-white/5 hover:bg-surface-container-high transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-on-surface font-headline">
                          {product.name}
                        </h4>
                        <p className="text-sm text-on-surface-variant mt-1">
                          {product.description}
                        </p>
                        <p className="text-base font-bold text-primary mt-2">
                          R$ {parseFloat(product.basePrice).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => openProductModal(product, index)}
                          className="p-2 text-on-surface-variant hover:text-primary hover:bg-surface-container rounded-lg transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(index)}
                          className="p-2 text-on-surface-variant hover:text-error hover:bg-surface-container rounded-lg transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>
                    </div>

                    {product.priceVariations.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-2">
                          Variações de Preço
                        </p>
                        {product.priceVariations.map((variation, vIndex) => (
                          <div
                            key={vIndex}
                            className="text-xs text-on-surface-variant"
                          >
                            • {variation.condition}: R${" "}
                            {parseFloat(variation.price).toFixed(2)}
                          </div>
                        ))}
                      </div>
                    )}

                    {product.extras.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-2">
                          Extras
                        </p>
                        {product.extras.map((extra, eIndex) => (
                          <div
                            key={eIndex}
                            className="text-xs text-on-surface-variant"
                          >
                            • {extra.name}: R${" "}
                            {parseFloat(extra.price).toFixed(2)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* SECTION 4: Operacional e Logística */}
          <section className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                <span className="material-symbols-outlined">settings_suggest</span>
              </div>
              <h2 className="font-headline text-xl font-bold text-on-surface">
                4. Operacional e Logística
              </h2>
            </div>
            <div className="space-y-6">
              <div>
                <label className={labelCls}>Canal de Vendas Principal (Link) *</label>
                <input
                  type="url"
                  value={operational.salesChannel}
                  onChange={(e) => {
                    setOperational({ ...operational, salesChannel: e.target.value });
                    setIsDirty(true);
                  }}
                  placeholder="Ex: https://delivery.livn.com.br/..."
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>Área de Atuação *</label>
                <input
                  type="text"
                  value={operational.serviceArea}
                  onChange={(e) => {
                    setOperational({ ...operational, serviceArea: e.target.value });
                    setIsDirty(true);
                  }}
                  placeholder='Ex: "Raio de 10km" ou "Atendemos todo o Brasil"'
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Política de Frete/Taxa</label>
                  <select
                    value={operational.shippingType}
                    onChange={(e) => {
                      setOperational({ ...operational, shippingType: e.target.value });
                      setIsDirty(true);
                    }}
                    className={inputCls}
                  >
                    <option value="fixed">Fixo</option>
                    <option value="km">Por km</option>
                    <option value="free">Grátis</option>
                    <option value="custom">Especifique</option>
                  </select>
                </div>

                {(operational.shippingType === "fixed" ||
                  operational.shippingType === "km" ||
                  operational.shippingType === "custom") && (
                  <div>
                    <label className={labelCls}>
                      {operational.shippingType === "fixed"
                        ? "Valor do Frete"
                        : operational.shippingType === "km"
                        ? "Valor por km"
                        : "Descrição da Taxa"}
                    </label>
                    <input
                      type={
                        operational.shippingType === "custom" ? "text" : "number"
                      }
                      step="0.01"
                      value={operational.shippingValue}
                      onChange={(e) => {
                        setOperational({
                          ...operational,
                          shippingValue: e.target.value,
                        });
                        setIsDirty(true);
                      }}
                      placeholder={
                        operational.shippingType === "fixed"
                          ? "Valor do frete"
                          : operational.shippingType === "km"
                          ? "Valor por km (Ex: 2.50)"
                          : "Descreva a taxa de entrega"
                      }
                      className={inputCls}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls + " flex items-center gap-1"}>
                  <span className="material-symbols-outlined text-sm">payments</span>
                  Formas de Pagamento Aceitas *
                </label>
                <div className="grid grid-cols-1 gap-2 mt-1">
                  {[
                    { key: "pix", label: "PIX" },
                    { key: "credit", label: "Cartão de Crédito" },
                    { key: "debit", label: "Cartão de Débito" },
                    { key: "cash", label: "Dinheiro" },
                  ].map((method) => (
                    <label
                      key={method.key}
                      className="flex items-center gap-3 p-3 bg-surface-container-highest rounded-lg cursor-pointer hover:bg-surface-container-high transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={operational.paymentMethods[method.key]}
                        onChange={(e) => {
                          setOperational({
                            ...operational,
                            paymentMethods: {
                              ...operational.paymentMethods,
                              [method.key]: e.target.checked,
                            },
                          });
                          setIsDirty(true);
                        }}
                        className="w-4 h-4 rounded accent-primary focus:ring-primary"
                      />
                      <span className="text-sm text-on-surface">{method.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Instruções de Pagamento</label>
                <textarea
                  value={operational.paymentInstructions}
                  onChange={(e) => {
                    setOperational({
                      ...operational,
                      paymentInstructions: e.target.value,
                    });
                    setIsDirty(true);
                  }}
                  placeholder='Ex: "Entregador leva maquininha" ou "Não aceitamos PIX remoto"'
                  rows={3}
                  className={inputCls + " resize-none"}
                />
              </div>
            </div>
          </section>

          {/* SECTION 5: Observações Gerais */}
          <section className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400">
                <span className="material-symbols-outlined">info</span>
              </div>
              <h2 className="font-headline text-xl font-bold text-on-surface">
                5. Observações Gerais (Opcional)
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Observações Extras</label>
                <textarea
                  value={observations}
                  onChange={(e) => { setObservations(e.target.value); setIsDirty(true); }}
                  placeholder="Adicione políticas específicas, observações de acompanhamento, etc..."
                  rows={8}
                  className={inputCls + " resize-none leading-relaxed"}
                />
                <p className="text-xs text-zinc-500 italic mt-1">
                  Use este campo para adicionar informações importantes que não se
                  encaixam nas outras categorias
                </p>
              </div>
            </div>
          </section>

          {/* Botão Salvar Final */}
          <div className="flex flex-col items-end gap-4 pt-4 mb-20">
            <p className="text-xs text-zinc-500 font-medium">
              * Campos obrigatórios. Certifique-se de preencher tudo antes de salvar.
            </p>
            <button
              onClick={saveBusinessProfile}
              disabled={saving}
              className="bg-primary text-on-primary hover:bg-primary/90 rounded-xl px-10 py-4 font-bold text-sm shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-3 disabled:opacity-50 disabled:pointer-events-none"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-on-primary border-t-transparent"></div>
                  Salvando Perfil Completo...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    save
                  </span>
                  SALVAR PERFIL COMPLETO
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Produto — renderizado no body via Portal para escapar do overflow-y-auto do <main> */}
      {showProductModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container rounded-xl border border-white/5 shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/5">
              <h2 className="font-headline text-xl font-bold text-on-surface">
                {editingProduct !== null ? "Editar Produto" : "Novo Produto"}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className={labelCls}>Nome do Item *</label>
                <input
                  type="text"
                  value={currentProduct.name}
                  onChange={(e) =>
                    setCurrentProduct({ ...currentProduct, name: e.target.value })
                  }
                  placeholder="Ex: Buffet Livre / Rodízio"
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>Descrição do que inclui</label>
                <textarea
                  value={currentProduct.description}
                  onChange={(e) =>
                    setCurrentProduct({
                      ...currentProduct,
                      description: e.target.value,
                    })
                  }
                  placeholder="Ex: +30 tipos de sushis, inclui 1 temaki e 4 sashimis"
                  rows={3}
                  className={inputCls + " resize-none"}
                />
              </div>

              <div>
                <label className={labelCls}>Preço Base (Padrão) *</label>
                <div className="relative">
                  <span className="absolute top-1/2 -translate-y-1/2 left-3 text-zinc-400 text-sm font-semibold">
                    R$
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={currentProduct.basePrice}
                    onChange={(e) =>
                      setCurrentProduct({
                        ...currentProduct,
                        basePrice: e.target.value,
                      })
                    }
                    placeholder="84.99"
                    className={inputCls + " pl-9"}
                  />
                </div>
              </div>

              {/* Variações de Preço */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className={labelCls + " mb-0"}>Variações de Preço</label>
                  <button
                    onClick={addPriceVariation}
                    className="bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest rounded-lg px-4 py-2 text-sm flex items-center gap-1 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">add</span>
                    Adicionar Regra
                  </button>
                </div>

                {currentProduct.priceVariations.length === 0 ? (
                  <p className="text-sm text-zinc-500 italic">
                    Nenhuma variação cadastrada
                  </p>
                ) : (
                  <div className="space-y-3">
                    {currentProduct.priceVariations.map((variation, index) => (
                      <div
                        key={index}
                        className="flex gap-2 items-start p-3 bg-surface-container-highest rounded-lg"
                      >
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={variation.condition}
                            onChange={(e) =>
                              updatePriceVariation(index, "condition", e.target.value)
                            }
                            placeholder="Ex: Segunda-feira / Crianças 6-9 anos"
                            className={inputCls}
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={variation.price}
                            onChange={(e) =>
                              updatePriceVariation(index, "price", e.target.value)
                            }
                            placeholder="Novo valor"
                            className={inputCls}
                          />
                        </div>
                        <button
                          onClick={() => removePriceVariation(index)}
                          className="p-2 text-on-surface-variant hover:text-error hover:bg-surface-container rounded-lg transition-colors mt-1"
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Itens Adicionais/Extras */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className={labelCls + " mb-0"}>Itens Adicionais/Extras</label>
                  <button
                    onClick={addExtra}
                    className="bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest rounded-lg px-4 py-2 text-sm flex items-center gap-1 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">add</span>
                    Adicionar Extra
                  </button>
                </div>

                {currentProduct.extras.length === 0 ? (
                  <p className="text-sm text-zinc-500 italic">
                    Nenhum extra cadastrado
                  </p>
                ) : (
                  <div className="space-y-3">
                    {currentProduct.extras.map((extra, index) => (
                      <div
                        key={index}
                        className="flex gap-2 items-center p-3 bg-surface-container-highest rounded-lg"
                      >
                        <input
                          type="text"
                          value={extra.name}
                          onChange={(e) =>
                            updateExtra(index, "name", e.target.value)
                          }
                          placeholder="Ex: Molho Tarê"
                          className={inputCls + " flex-1"}
                        />
                        <input
                          type="number"
                          step="0.01"
                          value={extra.price}
                          onChange={(e) =>
                            updateExtra(index, "price", e.target.value)
                          }
                          placeholder="Preço"
                          className={
                            "bg-surface-container-highest border-none rounded-lg px-4 py-2.5 text-sm text-on-surface focus:ring-1 focus:ring-primary focus:outline-none w-28"
                          }
                        />
                        <button
                          onClick={() => removeExtra(index)}
                          className="p-2 text-on-surface-variant hover:text-error hover:bg-surface-container rounded-lg transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-white/5 flex gap-3 justify-end">
              <button
                onClick={closeProductModal}
                className="bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest rounded-lg px-5 py-2.5 text-sm transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveProduct}
                className="bg-primary text-on-primary hover:bg-primary/90 rounded-lg px-5 py-2.5 font-semibold text-sm transition-colors"
              >
                Salvar Produto
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}


      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setProductToDeleteIndex(null);
        }}
        onConfirm={confirmDeleteProduct}
        title="Excluir Produto"
        message="Tem certeza que deseja excluir este produto? Esta ação não poderá ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal de Alterações Não Salvas — renderizado no body via Portal */}
      {showUnsavedModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop com blur */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancelLeave}
          />

          {/* Card do modal */}
          <div
            className="relative bg-surface-container rounded-2xl border border-white/10 shadow-2xl max-w-md w-full p-0 overflow-hidden"
            style={{ animation: "modalPop 0.2s cubic-bezier(0.34,1.56,0.64,1) both" }}
          >
            {/* Faixa de aviso no topo */}
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />

            <div className="p-6">
              {/* Ícone e título */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  <span
                    className="material-symbols-outlined text-amber-400 text-2xl"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    warning
                  </span>
                </div>
                <div>
                  <h3 className="font-headline text-lg font-bold text-on-surface mb-1">
                    Alterações não salvas
                  </h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Você fez alterações no perfil do negócio que ainda <strong className="text-on-surface">não foram salvas</strong>. Se sair agora, todas as modificações serão perdidas.
                  </p>
                </div>
              </div>

              {/* Aviso em destaque */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-6 flex items-center gap-2">
                <span className="material-symbols-outlined text-amber-400 text-base flex-shrink-0">info</span>
                <p className="text-xs text-amber-300/90">
                  Clique em <strong>"Continuar aqui"</strong> para voltar e salvar, ou em <strong>"Sair sem salvar"</strong> para descartar.
                </p>
              </div>

              {/* Botões */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCancelLeave}
                  className="flex-1 bg-primary text-on-primary hover:bg-primary/90 rounded-xl px-5 py-3 font-semibold text-sm transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                >
                  <span className="material-symbols-outlined text-base">arrow_back</span>
                  Continuar aqui
                </button>
                <button
                  onClick={handleConfirmLeave}
                  className="flex-1 bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest rounded-xl px-5 py-3 font-semibold text-sm transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-base">logout</span>
                  Sair sem salvar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.92) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default BusinessProfile;
